import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import CloseIcon from '@mui/icons-material/CloseRounded';
import Markdown from 'markdown-to-jsx';
import { typography, mediaQuery } from '../../theme';
import LinkNoPrefetch from '../linkNoPrefetch/LinkNoPrefetch';
import { formatUrl, isExternalLink } from '../../utils/utils';
import { colors } from '../../theme/color';

export enum contextType {
  DANGER = 'danger',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
  NOTIFICATION = 'notification',
}

export const contextBgColors = {
  [contextType.DANGER]: colors.warningRed,
  [contextType.WARNING]: colors.orderSummaryAlert,
  [contextType.SUCCESS]: colors.green.base,
  [contextType.INFO]: colors.black,
  [contextType.NOTIFICATION]: colors.brandDarkGreen,
} as const;

type BaseProps = {
  context?: contextType | undefined;
  tight?: boolean;
  // Native homepage: bleed the alert background up into the safe-area notch (text stays below it).
  notchFill?: boolean;
};

type SnackbarProps = BaseProps & {
  className?: string;
  message: string;
  closable?: boolean;
  id?: string;
  icon?: React.ReactNode;
  align?: 'left' | 'center';
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export const Snackbar: React.FC<SnackbarProps> = ({
  message,
  id,
  closable = true,
  icon,
  align = 'left',
  tight,
  onClick,
  ...props
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!id || !window.sessionStorage.getItem(`snackbar_hidden_${id}`)) {
      queueMicrotask(() => {
        setVisible(true);
      });
    }
  }, [id]);

  if (!visible) return null;

  const closeAlert = (): void => {
    setVisible(false);
    window.sessionStorage.setItem(`snackbar_hidden_${id}`, 'true');
  };

  const iconColor = props.context === contextType.WARNING ? 'black' : 'white';

  return (
    <Container {...props} tight={tight}>
      <Content align={align} closable={closable}>
        {icon && <IconContainer>{icon}</IconContainer>}
        <Markdown
          options={{
            overrides: {
              a: {
                component: RouterLink,
                props: {
                  onClick,
                },
              },
            },
          }}
        >
          {message}
        </Markdown>
      </Content>
      {closable && (
        <CloseButton onClick={closeAlert} aria-label="Piilota banneri">
          <CloseIcon htmlColor={iconColor} aria-hidden="true" />
        </CloseButton>
      )}
    </Container>
  );
};

type RouterLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

export const RouterLink: React.FC<RouterLinkProps> = ({ href, ...props }) => {
  const url = formatUrl(href);
  return isExternalLink(url) ? <a {...props} href={url} /> : <LinkNoPrefetch {...props} href={url} />;
};

const Container = styled.div<BaseProps>`
  width: 100%;
  display: flex;
  padding: 24px;
  color: white;
  justify-content: center;
  border-bottom: 1px solid white;
  position: relative;

  & p,
  & span {
    ${typography.paragraphMediumStyle}
    margin: 0 8px 0 0;
  }

  ${({ tight }) => {
    if (tight)
      return css`
        padding: 16px 24px;
        & p,
        & span {
          ${typography.paragraphMediumStyle}
        }
        @media ${mediaQuery.medium} {
          & p,
          & span {
            ${typography.paragraphSmallStyle}
          }
        }
      `;
  }}

  & a {
    ${typography.linkStyle}
    color: white;
    border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
    transition: color 0.1s;

    &:hover,
    &:active,
    &:focus {
      color: #eee;
      outline-color: #eee;
    }
  }

  ${({ context }) => {
    switch (context) {
      case contextType.DANGER:
        return css`
          background: ${contextBgColors[contextType.DANGER]};
        `;
      case contextType.NOTIFICATION:
        return css`
          background: ${contextBgColors[contextType.NOTIFICATION]};
        `;
      case contextType.WARNING:
        return css`
          background: ${contextBgColors[contextType.WARNING]};
          color: ${colors.textDefault};

          & a {
            color: ${colors.textDefault};
            transition: 0.1s all;

            &:hover,
            &:active,
            &:focus {
              color: ${({ theme }) => theme.color?.button.selected};
              outline-color: ${({ theme }) => theme.color?.button.selected};
              outline-offset: 5px;
            }
          }
        `;
      case contextType.SUCCESS:
        return css`
          background: ${contextBgColors[contextType.SUCCESS]};
          color: ${colors.textDefault};
        `;

      default:
        return css`
          background: black;
        `;
    }
  }}

  ${({ notchFill }) =>
    notchFill &&
    css`
      /* Fill the safe-area notch above the alert with the alert's own background colour, without
         altering the alert's own padding (so the centered close button stays aligned with the text). */
      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 100%;
        height: var(--safe-area-inset-top, env(safe-area-inset-top, 0px));
        background: inherit;
      }
    `}
`;

const Content = styled.div<{ align: 'left' | 'center'; closable: boolean }>(
  ({ align, closable }) => css`
    display: flex;
    justify-content: ${align === 'center' ? 'center' : 'flex-start'};
    align-items: center;
    max-width: 1200px;
    width: 100%;

    @media ${mediaQuery.extralarge} {
      max-width: calc(100% - ${closable ? '4em' : '0'});
    }

    @media ${mediaQuery.medium} {
      justify-content: ${align === 'center' ? 'center' : 'flex-start'};
      max-width: 100%;
      margin-right: ${align === 'center' ? '0px' : '32px'};
    }
  `
);

const IconContainer = styled.div`
  margin-right: 16px;

  & svg {
    font-size: 2rem;
  }
`;

const CloseButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
  position: absolute;
  top: 50%;
  margin-top: -1em;
  right: 1em;
  width: 2em;
  height: 2em;
  flex-shrink: 0;
  border-radius: 4px;

  & svg {
    width: 100%;
    height: 100%;
    transition: color 0.1s;
  }

  &:hover,
  &:active,
  &:focus {
    & svg {
      color: #eee;
    }
  }

  &:focus {
    outline: 2px solid #eee;
  }
`;
